import { BorderedBox, BorderedBoxEntry } from "../components";
import { EditableField } from "./components";
import { CustomAlert } from "components";
import { Row, Col, TabPane, Button } from "reactstrap";
import { useFieldArray } from "react-hook-form";
import { useEffect, useReducer } from "react";
import { FiX } from "react-icons/fi";
import {
  confirmationModalInitialState,
  confirmationModalReducer,
  confirmationModalReducerActions,
} from "./reducers";

export const StudentReport = ({
  studentRecord: { studentReport, studentReportAdded, studentReportApproved },
  canEdit = false,
  register,
  control,
  resetFields = () => {},
  formState = { errors: {} },
  watch,
  editable,
  setEditable,
  setValue,
}) => {
  const { errors, isDirty } = formState;
  const traitsPrefix = "studentReport.traits";
  const learningObjectivePrefix = "studentReport.learning_objectives";
  const additionalCommentsPrefix = "studentReport.additional_comments";
  const classQuestionsPrefix = "studentReport.class_questions";
  const { fields: learningObjectives, remove: removeLearningObjective } =
    useFieldArray({
      control,
      name: learningObjectivePrefix,
    });
  const { fields: additionalComments, remove: removeAdditionalComment } =
    useFieldArray({
      control,
      name: additionalCommentsPrefix,
    });
  const { fields: classQuestions, remove: removeClassQuestion } = useFieldArray(
    {
      control,
      name: classQuestionsPrefix,
    },
  );
  const { fields: traits } = useFieldArray({
    control,
    name: traitsPrefix,
  });
  const [confirmationModalProps, confirmationModalDispatch] = useReducer(
    confirmationModalReducer,
    confirmationModalInitialState,
  );

  const triggerConfirmationModal = (index) => () => {
    confirmationModalDispatch({
      type: confirmationModalReducerActions.OPEN,
      payload: index,
    });
  };

  const closeConfirmationModal = () =>
    confirmationModalDispatch({
      type: confirmationModalReducerActions.CLOSE,
    });

  const submitConfirmationModal = (index) => () => {
    removeLearningObjective(index);
    confirmationModalDispatch({
      type: confirmationModalReducerActions.CLOSE,
    });
  };

  useEffect(() => {
    if (studentReport?.lesson_detail?.lesson?.is_custom_lesson) {
      setValue(
        "studentReport.custom_lesson_name",
        studentReport?.lesson_detail?.lesson?.name,
      );
      setValue(
        "studentReport.is_custom_lesson",
        studentReport?.lesson_detail?.lesson?.is_custom_lesson,
      );
    }
  }, [studentReport?.lesson_detail?.lesson?.name]);

  const extendedResetFields = () => {
    if (studentReport?.lesson_detail?.lesson?.is_custom_lesson) {
      setValue(
        "studentReport.custom_lesson_name",
        studentReport?.lesson_detail?.lesson?.name,
      );
      setValue(
        "studentReport.is_custom_lesson",
        studentReport?.lesson_detail?.lesson?.is_custom_lesson,
      );
    }
    resetFields();
  };

  useEffect(() => {
    setEditable(false);
  }, []);

  return (
    <TabPane tabId={1} key={1}>
      <CustomAlert
        show={confirmationModalProps.show}
        hideModal={closeConfirmationModal}
        title="Confirmation"
        description="Delete Learning Objective?"
        submitButtonText="Delete"
        submitModal={submitConfirmationModal(
          confirmationModalProps.indexToDelete,
        )}
        fitToParent={true}
      />
      {studentReportAdded ? (
        <Row>
          <Col sm="12">
            {canEdit && !studentReportApproved && (
              <div className="d-flex justify-content-end mb-3">
                {canEdit && !editable && (
                  <>
                    <Button
                      color="warning"
                      size="sm"
                      type="button"
                      onClick={() => {
                        setEditable(true);
                      }}
                    >
                      Edit
                    </Button>
                  </>
                )}

                <Button
                  color="secondary"
                  size="sm"
                  type="button"
                  onClick={() => {
                    setEditable(false);
                    extendedResetFields();
                  }}
                >
                  Reset
                </Button>
              </div>
            )}
            <BorderedBox>
              <BorderedBoxEntry
                label="Course"
                value={studentReport?.lesson_detail?.course?.name}
              />
              <BorderedBoxEntry
                label="Lesson"
                value={
                  <EditableField
                    editable={
                      studentReport.lesson_detail?.lesson?.is_custom_lesson &&
                      editable
                    }
                    register={register}
                    name="studentReport.lesson_detail.lesson.name"
                    error={errors?.studentReport?.lesson_detail?.lesson?.name}
                    alt={watch("studentReport.lesson_detail.lesson.name")}
                    required={true}
                  />
                }
              />
            </BorderedBox>

            <div
              className="col-12 py-2  mb-4"
              style={{
                border: "2px solid #ffffff25",
                borderRadius: 8,
              }}
            >
              <div
                className="row pb-1"
                style={{
                  borderBottom: "2px solid #ffffff25",
                }}
              >
                <dt className="col-4 text-primary">Trait Name</dt>
                <dt className="col-4 text-primary">Performance</dt>
                <dt className="col-4 text-primary">Comments</dt>
              </div>
              {traits.map((trait, index) => (
                <div key={trait.id} className="row pt-2">
                  <span className="col-4 text-white">
                    <EditableField
                      editable={false}
                      register={register}
                      name={`${traitsPrefix}.${index}.name`}
                      error={
                        errors?.studentReport?.[traitsPrefix]?.[index]?.name
                      }
                      alt={watch(`${traitsPrefix}.${index}.name`)}
                      required={true}
                    />
                  </span>
                  <span className="col-4  text-white">
                    <EditableField
                      editable={editable}
                      register={register}
                      name={`${traitsPrefix}.${index}.performance`}
                      error={
                        errors?.studentReport?.[traitsPrefix]?.[index]
                          ?.performance
                      }
                      alt={watch(`${traitsPrefix}.${index}.performance`)}
                      required={true}
                    />
                  </span>
                  <span className="col-4  text-white">
                    <EditableField
                      editable={editable}
                      register={register}
                      name={`${traitsPrefix}.${index}.comments`}
                      error={
                        errors?.studentReport?.[traitsPrefix]?.[index]?.comments
                      }
                      alt={watch(`${traitsPrefix}.${index}.comments`)}
                    />
                  </span>
                </div>
              ))}
            </div>
            <div
              className="col-12 py-2 mb-4"
              style={{
                border: "2px solid #ffffff25",
                borderRadius: 8,
              }}
            >
              <div
                className="row pb-1"
                style={{
                  borderBottom: "2px solid #ffffff25",
                }}
              >
                <dt className="col-8 text-primary">Learning Objective</dt>
                <dt className="col-4 text-primary">Performance</dt>
              </div>
              {learningObjectives?.map((learningObjective, index) => (
                <div key={learningObjective.id} className="row pt-2">
                  <span className="col-8 text-white">
                    <EditableField
                      editable={editable}
                      register={register}
                      name={`${learningObjectivePrefix}.${index}.name`}
                      error={
                        errors?.studentReport?.[learningObjectivePrefix]?.[
                          index
                        ]?.name
                      }
                      alt={watch(`${learningObjectivePrefix}.${index}.name`)}
                      required={true}
                    />
                  </span>
                  <span className="col text-white">
                    <EditableField
                      editable={editable}
                      register={register}
                      name={`${learningObjectivePrefix}.${index}.performance`}
                      error={
                        errors?.studentReport?.[learningObjectivePrefix]?.[
                          index
                        ]?.performance
                      }
                      alt={watch(
                        `${learningObjectivePrefix}.${index}.performance`,
                      )}
                      required={true}
                      type="textarea"
                    />
                  </span>
                  {editable && (
                    <span className="col-1">
                      <FiX
                        className="text-danger"
                        onClick={triggerConfirmationModal(index)}
                        style={{
                          cursor: "pointer",
                        }}
                      />
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div
              className="col-12 py-2 mb-4"
              style={{
                border: "2px solid #ffffff25",
                borderRadius: 8,
              }}
            >
              <div
                className="row pb-1"
                style={{
                  borderBottom: "2px solid #ffffff25",
                }}
              >
                <dt className="col-8 text-primary">Class Questions</dt>
                <dt className="col-4 text-primary">Answer</dt>
              </div>
              {classQuestions?.map((classQuestion, index) => (
                <div key={classQuestion.id} className="row pt-2">
                  <span className="col-8 text-white">
                    <EditableField
                      editable={editable}
                      register={register}
                      name={`${classQuestionsPrefix}.${index}.question`}
                      error={
                        errors?.studentReport?.[classQuestionsPrefix]?.[index]
                          ?.question
                      }
                      alt={watch(`${classQuestionsPrefix}.${index}.question`)}
                      required={true}
                    />
                  </span>
                  <span className="col text-white">
                    <EditableField
                      editable={editable}
                      register={register}
                      name={`${classQuestionsPrefix}.${index}.answer`}
                      error={
                        errors?.studentReport?.[classQuestionsPrefix]?.[index]
                          ?.answer
                      }
                      alt={watch(`${classQuestionsPrefix}.${index}.answer`)}
                      required={true}
                      type="textarea"
                    />
                  </span>
                  {editable && (
                    <span className="col-1">
                      <FiX
                        className="text-danger"
                        onClick={triggerConfirmationModal(index)}
                        style={{
                          cursor: "pointer",
                        }}
                      />
                    </span>
                  )}
                </div>
              ))}
            </div>
            <BorderedBox>
              {additionalComments?.map((comment, index) => (
                <BorderedBoxEntry
                  fullWidth
                  key={comment.id}
                  label={comment.question}
                  value={
                    <EditableField
                      editable={editable}
                      register={register}
                      name={`${additionalCommentsPrefix}.${index}.answer`}
                      error={
                        errors?.studentReport?.[additionalCommentsPrefix]?.[
                          index
                        ]?.answer
                      }
                      alt={watch(`${additionalCommentsPrefix}.${index}.answer`)}
                      required={true}
                      type="textarea"
                    />
                  }
                  marginClass="mb-4"
                />
              ))}
            </BorderedBox>
          </Col>
        </Row>
      ) : (
        <BorderedBox>
          <div className="ml-2">Student Report Not Added Yet</div>
        </BorderedBox>
      )}
    </TabPane>
  );
};
